import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useUserStore } from '../stores/StoreProvider';
import { auth0Service } from '../services/auth0Service';

const ProtectedRoute = observer(({ children, requireAuth = true, requireProfile = true }) => {
  const userStore = useUserStore();
  const location = useLocation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  // Handle Auth0 authentication state
  React.useEffect(() => {
    const syncAuthState = async () => {
      try {
        console.log('🔍 ProtectedRoute: Syncing auth state...');
        const authenticated = auth0Service.isAuthenticated();
        console.log('🔍 ProtectedRoute: Auth0 authenticated:', authenticated);
        setIsAuthenticated(authenticated);
        
        if (authenticated) {
          const accessToken = auth0Service.getAccessToken();
          const user = auth0Service.getCurrentUser();
          console.log('🔍 ProtectedRoute: Setting auth state with token:', !!accessToken);
          userStore.setAuthState(true, accessToken, user);
          
          // Fetch user profile from backend if we have a token
          if (accessToken) {
            console.log('🔍 ProtectedRoute: Fetching profile...');
            await userStore.fetchProfile();
          }
        } else {
          console.log('🔍 ProtectedRoute: Not authenticated, clearing auth state');
          userStore.setAuthState(false);
        }
      } catch (error) {
        console.error('Error syncing auth state:', error);
        userStore.setAuthState(false);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    syncAuthState();
  }, [userStore]);

  // Show loading while Auth0 is initializing
  if (isLoading) {
    console.log('🔍 ProtectedRoute: Still loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If auth is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    console.log('🔍 ProtectedRoute: Redirecting to login because not authenticated', {
      requireAuth,
      isAuthenticated,
      userStoreAuth: userStore.isAuthenticated,
      accessToken: !!userStore.accessToken
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If profile is required but user doesn't have one
  if (requireProfile && isAuthenticated && !userStore.hasProfile) {
    console.log('🔍 ProtectedRoute: Redirecting to onboarding because:', {
      requireProfile,
      isAuthenticated,
      hasProfile: userStore.hasProfile,
      profile: userStore.profile
    });
    return <Navigate to="/onboarding/names" replace />;
  }

  // If user is authenticated and has profile, but trying to access auth pages
  if (isAuthenticated && userStore.hasProfile && location.pathname.startsWith('/login')) {
    return <Navigate to="/app/dashboard" replace />;
  }

  console.log('🔍 ProtectedRoute: Allowing access to route', {
    pathname: location.pathname,
    requireAuth,
    requireProfile,
    isAuthenticated,
    hasProfile: userStore.hasProfile
  });
  return children;
});

export default ProtectedRoute; 